import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';

import { errorText } from '../components/custom_creative_session/DevelopmentStage';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * The Prompts step's behaviour — ONE implementation, two screens.
 *
 * WAVE 3, SCREEN 7, the last of the wizard. This is where an operator sees what
 * their session actually produced, sends it to Drive, and FINISHES the session.
 *
 * WHAT THE SWEEP FOUND:
 *
 *   1. `fetchData` caught every failure into `console.error` and left the list
 *      empty, so an unreachable backend looked exactly like a session that
 *      produced nothing — on the one screen whose entire job is to show what
 *      was produced. Rule 10, at the worst possible place.
 *   2. The per-production delivery-audit read was `.catch(() => ({ audit_entries:
 *      [] }))` — a failed audit became "no audit entries", silently, for every
 *      production. A delivery that was never checked and a delivery that
 *      checked out clean rendered identically.
 *   3. Scoping was still the OLD timestamp-only kind:
 *      `if (isNewSession && sessionStartTime)`. Productions carry no
 *      `session_id`, so a RESUMED session showed every production the offer
 *      had ever had. They do carry `script_id`, and scripts carry the session —
 *      so the link exists and simply was not followed.
 *
 * WHAT WAS ALREADY RIGHT, and the sweep says so: `finishSession` names its own
 * failure. That was item 11's fix, and it holds — a finish that failed quietly
 * would leave the session `active` and look exactly like one that worked, which
 * is how four sessions stayed open for two months.
 */
//: How long after a production is created its delivery is still plausibly in
//: flight. Measured on the Founder's submit: the record was written at 17:38:07
//: and the Trello card landed at 17:38:19 — twelve seconds. Sixty gives room
//: for a slow Drive without holding a false "still going" for a genuine failure.
export const DELIVERY_WINDOW_MS = 60_000;

export function usePrompts({ offer, sessionId, isNewSession, sessionStartTime,
                             onFinished, refreshKey = 0 }) {
  const [productions, setProductions] = useState([]);
  const [scopeNote, setScopeNote] = useState(null);
  const [audit, setAudit] = useState([]);
  const [auditFailed, setAuditFailed] = useState(0);

  const [loadState, setLoadState] = useState('idle');
  const [loadError, setLoadError] = useState(null);
  const [actionError, setActionError] = useState(null);
  const [busy, setBusy] = useState(null);

  const [finishing, setFinishing] = useState(false);
  const [finishResult, setFinishResult] = useState(null);
  const [finishError, setFinishError] = useState(null);

  /**
   * Is this production still being delivered, rather than not delivered?
   *
   * THE DEFECT THIS EXISTS FOR, found on the Founder's own submit
   * (prod_43c692963705, 2026-09-10). The screen said "not in Drive yet" and
   * "no Trello card — nobody has been handed this yet". **Both were false.**
   * The delivery audit shows DRIVE_UPLOAD SUCCESS at 17:38:13, a second
   * DRIVE_UPLOAD SUCCESS at :16, TRELLO_CARD SUCCESS at :19 and
   * TELEGRAM_NOTIFY SUCCESS at :19 — and a Drive URL and a Trello URL are on
   * the record.
   *
   * The production row is inserted BEFORE those steps run, and this screen read
   * it in between. So it asserted a negative from data it had simply read too
   * early — which is worse than a blank, because a blank does not claim
   * anything. Rule 10: "delivered", "still going" and "did not happen" are
   * three states, and only one of them was being drawn.
   */
  const stillDelivering = (p) => {
    if (p.google_drive_url && p.trello_card_url) return false;
    const started = Date.parse(p.created_at || p.submitted_at || '');
    if (!started) return false;                 // no timestamp, no guessing
    return Date.now() - started < DELIVERY_WINDOW_MS;
  };

  const load = useCallback(async () => {
    setLoadState('loading');
    try {
      // Productions carry `script_id`; scripts carry `session_id`. Following
      // that link is what lets a RESUMED session be scoped at all — the old
      // code could only filter a brand-new one, by timestamp.
      const [prodRes, scriptRes] = await Promise.all([
        axios.get(`${API}/offers/${offer.offer_id}/productions`),
        axios.get(`${API}/offers/${offer.offer_id}/scripts`),
      ]);
      const all = prodRes.data.productions || [];
      const scripts = scriptRes.data.scripts || [];
      const sessionOf = new Map(scripts.map((s) => [s.script_id, s.session_id]));

      let rows = all;
      let note = null;
      if (sessionId) {
        const mine = all.filter((p) => sessionOf.get(p.script_id) === sessionId);
        const unknown = all.filter((p) => !sessionOf.get(p.script_id));
        if (mine.length > 0) {
          rows = mine;
          const others = all.length - mine.length;
          if (others > 0) note = `Not shown: ${others} production(s) from other work on this offer.`;
        } else if (unknown.length > 0) {
          // The same rule every other step uses: an empty screen with the
          // answer sitting in the response is a bug, not a scoping rule.
          rows = unknown;
          note = `Showing ${unknown.length} production(s) whose session cannot be `
               + `determined — their scripts predate session tracking.`;
        } else {
          rows = [];
        }
      } else if (isNewSession && sessionStartTime) {
        const cutoff = new Date(sessionStartTime);
        rows = all.filter((p) => p.created_at && new Date(p.created_at) >= cutoff);
      }

      setProductions(rows);
      setScopeNote(note);
      setLoadState('ok');
      setLoadError(null);

      // THE AUDIT, with failures COUNTED rather than flattened into "none".
      const results = await Promise.all(rows.map((p) =>
        axios.get(`${API}/delivery-audit`, { params: { production_id: p.production_id } })
          .then((r) => ({ ok: true, entries: r.data?.audit_entries || [] }))
          .catch(() => ({ ok: false, entries: [] }))));
      setAudit(results.flatMap((r) => r.entries));
      setAuditFailed(results.filter((r) => !r.ok).length);
    } catch (error) {
      setLoadState('failed');
      setLoadError(errorText(error));
    }
  }, [offer.offer_id, sessionId, isNewSession, sessionStartTime]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // ONE re-read, not a poll. Landing here straight after a submit means the
  // delivery is usually a few seconds behind the record; a single delayed
  // refresh turns "still delivering" into the real answer without the operator
  // pressing anything. Bounded deliberately — a loop would hide a delivery that
  // never completes behind an endless "still going".
  const [recheckDone, setRecheckDone] = useState(false);
  useEffect(() => {
    if (recheckDone || loadState !== 'ok') return undefined;
    if (!productions.some(stillDelivering)) return undefined;
    const t = setTimeout(() => { setRecheckDone(true); load(); }, 8000);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productions, loadState, recheckDone, load]);

  const uploadToDrive = async (productionId) => {
    setBusy(productionId);
    setActionError(null);
    try {
      await axios.post(`${API}/productions/${productionId}/upload-to-drive`);
      await load();
      return true;
    } catch (error) {
      setActionError(errorText(error));
      return false;
    } finally {
      setBusy(null);
    }
  };

  const retryAudit = async (auditId) => {
    setBusy(auditId);
    setActionError(null);
    try {
      await axios.post(`${API}/delivery-audit/${auditId}/retry`);
      await load();
      return true;
    } catch (error) {
      setActionError(errorText(error));
      return false;
    } finally {
      setBusy(null);
    }
  };

  /** Item 11's fix, carried verbatim: a finish that fails says so. */
  const finishSession = async () => {
    if (!sessionId) return false;
    setFinishing(true);
    setFinishError(null);
    try {
      const res = await axios.post(`${API}/creative/sessions/${sessionId}/complete`);
      setFinishResult(res.data);
      if (onFinished) onFinished(res.data);
      return true;
    } catch (error) {
      setFinishError(`Could not finish this session: ${errorText(error)}`);
      return false;
    } finally {
      setFinishing(false);
    }
  };

  return {
    productions, audit, auditFailed, scopeNote,
    loadState, loadError, retryLoad: load,
    busy, uploadToDrive, retryAudit, stillDelivering,
    finishing, finishResult, finishError, finishSession,
    actionError, setActionError,
  };
}
